import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { toolVisibleFromYear, type StudyTool } from './studyToolTypes';

/**
 * Server overlay over `STUDY_TOOLS` (`GET /study/tool-registry`; SIR-CUR-04). super_admin can hide
 * or retire a tool, change the Years it is shown for and move it up the list without an app
 * release. The overlay never adds a tool the app does not have, and it never turns on a tool the
 * app keeps behind a feature flag. When the overlay is not loaded, the app list applies unchanged.
 */
export type StudyToolRegistryStatus = 'live' | 'preview' | 'hidden' | 'retired';

export interface StudyToolRegistryEntry {
  tool_key: string;
  subject_key: string;
  status: StudyToolRegistryStatus;
  year_levels: number[];
  sort_order: number;
}

export interface StudyToolRegistry {
  tools: StudyToolRegistryEntry[];
}

export const STUDY_TOOL_REGISTRY_QUERY_KEY = ['study-tool-registry'] as const;
const REGISTRY_STALE_MS = 60_000;

export const getStudyToolRegistry = () => api<StudyToolRegistry>('/study/tool-registry');

/** A malformed answer counts as "not loaded", so the app list applies unchanged. */
export function validStudyToolRegistry(value: unknown): StudyToolRegistry | undefined {
  const tools = (value as { tools?: unknown } | null)?.tools;
  return Array.isArray(tools) ? { tools: tools as StudyToolRegistryEntry[] } : undefined;
}

export function useStudyToolRegistry() {
  return useQuery({
    queryKey: STUDY_TOOL_REGISTRY_QUERY_KEY,
    queryFn: getStudyToolRegistry,
    select: validStudyToolRegistry,
    retry: false,
    staleTime: REGISTRY_STALE_MS,
  });
}

function entryFor(
  registry: StudyToolRegistry | undefined,
  toolId: string,
): StudyToolRegistryEntry | undefined {
  return registry?.tools.find((entry) => entry.tool_key === toolId);
}

/** Hidden or retired by super_admin. */
export function registryHidesTool(registry: StudyToolRegistry | undefined, toolId: string): boolean {
  const status = entryFor(registry, toolId)?.status;
  return status === 'hidden' || status === 'retired';
}

/**
 * First Year a tool is shown for: the server Year tags, else the app's start Year (Year 6 for a
 * general tool, D-STUDY-GENERAL-TOOLS-20261007).
 */
export function registryFirstYear(registry: StudyToolRegistry | undefined, tool: StudyTool): number {
  const years = entryFor(registry, tool.id)?.year_levels ?? [];
  return years.length > 0 ? Math.min(...years) : toolVisibleFromYear(tool);
}

/** Whether a tool belongs to the selected Year (`null` = all Years). */
export function registryAllowsYear(
  registry: StudyToolRegistry | undefined,
  tool: StudyTool,
  year: number | null,
): boolean {
  if (year === null) return true;
  const years = entryFor(registry, tool.id)?.year_levels ?? [];
  return years.length > 0 ? years.includes(year) : year >= toolVisibleFromYear(tool);
}

/** Order tools by the server position; tools the server does not list keep app order after them. */
export function sortByRegistry(
  registry: StudyToolRegistry | undefined,
  tools: StudyTool[],
): StudyTool[] {
  if (!registry) return tools;
  const rank = new Map(registry.tools.map((entry) => [entry.tool_key, entry.sort_order]));
  return tools
    .map((tool, index) => ({ tool, index, rank: rank.get(tool.id) }))
    .sort((left, right) => {
      if (left.rank !== undefined && right.rank !== undefined) return left.rank - right.rank;
      if (left.rank !== undefined) return -1;
      if (right.rank !== undefined) return 1;
      return left.index - right.index;
    })
    .map((entry) => entry.tool);
}
